import React, {useState, useEffect, useCallback} from 'react';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import Drawer from '@material-ui/core/Drawer';
import Button from '@material-ui/core/Button';
import Icon from '@mdi/react';
import SGSubtitle from 'components/Typography/SGSubtitle';
import SeverityBadge from 'features/POSRegisters/components/SeverityBadge';
import Accordion from 'features/POSRegisters/components/Accordion';
import SGTitle from 'components/Typography/SGTitle';
import DeviceDetails from 'features/POSRegisters/components/DeviceDetails';
import NikePOSIcon from 'features/POSRegisters/POSDiagram/components/NikePOSIcon';
import {mdiMenuOpen, mdiChevronRight} from '@mdi/js';
import POSState from 'features/POSRegisters/interfaces/POSState';
import Device from 'features/POSRegisters/interfaces/Device';
import DeviceType from 'features/POSRegisters/interfaces/DeviceType';
import ScanDetails from 'features/POSRegisters/interfaces/ScanDetails';
import getPriorityAlert from 'features/POSRegisters/functions/getPriorityAlert';
import Severity from 'features/POSRegisters/interfaces/Severity';
import {AlertType} from 'features/POSRegisters/interfaces/AlertInfo';
import type Alert from 'features/POSRegisters/interfaces/AlertInfo';

interface AlertHeaderProps {
  type: string;
  title: string;
  subtitle: string;
  severity: Severity;
  deviceType: DeviceType;
}

type AlertItem = Alert &
  AlertHeaderProps & {id: string; scanResults: Partial<ScanDetails>; expanded: boolean};

//#region styles
const StyledButtonOpenDrawer = styled(Button)`
  position: absolute;
  top: 0;
  right: 0;
  height: 52px;
  width: 52px;
  padding: 0px;
  color: ${props => props.theme.white};
`;

const StyledButtonCloseDrawer = styled(Button)`
  height: 52px;
  width: 52px;
  padding: 0px;
  color: ${props => props.theme.white};
`;

const StyledAlertsNotification = styled.div`
  position: absolute;
  top: 8px;
  right: 14px;
`;

const StyledDrawer = styled(Drawer)`
  .MuiDrawer-root {
    position: relative;
  }

  .MuiDrawer-paper {
    width: 392px;
    background: ${props => props.theme.defaultNavy};
    overflow: hidden;
  }
`;

const StyledGridTitle = styled(Grid)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 24px;
  background: #022037;
  border-bottom: 1px solid ${props => props.theme.grey}80;
  color: ${props => props.theme.text};
  font-size: 20px;
  font-weight: 700;
  line-height: 20px;
  text-transform: uppercase;
`;

const StyledButtonCollapseAll = styled(Button)`
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  text-decoration: underline;
  color: ${props => props.theme.text};
  text-transform: none;
  margin-left: auto;
`;

const StyledAlertsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
  overflow-y: auto;
`;

const StyledGridAlert = styled(Grid)`
  display: flex;
  gap: 16px;
  padding: 8px 16px;
  background: #022037;
  border-bottom: 1px solid ${props => props.theme.grey}80;
`;

const StyledAlertHeader = styled(Grid)`
  display: flex;
  flex-direction: column;
  padding: 0 8px;
  color: ${props => props.theme.text};

  .title {
    display: flex;
    align-items: center;
    gap: 6px;

    > svg {
      height: 14px;
      width: 14px;
      color: ${props => props.theme.disabledStateBlue};
    }
  }

  .s3 {
    font-weight: 700;
    &.Minor {
      color: ${props => props.theme.yellow};
    }

    &.Major {
      color: ${props => props.theme.orange};
    }

    &.Critical {
      color: ${props => props.theme.red};
    }

    &.scan-failure {
      color: ${props => props.theme.informationalBlue};
    }
  }
`;

const StyledAlertDetailsContainer = styled(Grid)`
  display: grid;
  grid-template-columns: 1fr;
  padding-top: 8px;
  gap: 16px;
`;

const StyledAlertDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

//#endregion styles
function AlertHeader({type, title, subtitle, severity, deviceType}: AlertHeaderProps) {
  return (
    <StyledAlertHeader>
      <div className="title">
        <NikePOSIcon deviceType={deviceType} />
        <SGSubtitle className="s2">{title}</SGSubtitle>
      </div>
      {type === AlertType.SCAN_FAILURE_ALERT ? (
        <SGSubtitle className="s3 scan-failure">Scan Failure - {subtitle}</SGSubtitle>
      ) : (
        <SGSubtitle className={`s3 ${severity}`}>{subtitle}</SGSubtitle>
      )}
    </StyledAlertHeader>
  );
}

export default function Alerts({state}: {state: POSState}) {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const allAlertsExpanded = alerts.every(alert => !!alert.expanded);

  const handleExpandChange = useCallback(
    (expanded: boolean, alertId: string) => {
      const newState = alerts.map(alert => (alert.id === alertId ? {...alert, expanded} : alert));
      setAlerts(newState);
    },
    [alerts]
  );

  const handleToggleAllAlerts = useCallback(() => {
    const newState = alerts.map(alert => ({...alert, expanded: !allAlertsExpanded}));
    setAlerts(newState);
  }, [alerts]);

  useEffect(() => {
    const {
      networkCircuits,
      ipads,
      ipadsMini,
      receiptPrinters,
      barcodeScanners,
      cashDrawers,
      paymentTerminals,
    } = state;

    const devicesWithAlert = [
      ...networkCircuits,
      ...ipads,
      ...ipadsMini,
      ...receiptPrinters,
      ...barcodeScanners,
      ...cashDrawers,
      ...paymentTerminals,
    ].filter(item => item.alerts.length > 0);

    const list: AlertItem[] = [];

    devicesWithAlert.forEach((device, deviceIdx) => {
      device.alerts.forEach((alert, alertIdx) => {
        list.push({
          ...alert,
          id: `${device.scanResults.deviceName}-${deviceIdx}-${alertIdx}`,
          title: device.scanResults.deviceName!,
          scanResults: device.scanResults,
          type: alert.alertType,
          subtitle: alert.shortDescription,
          severity: alert.siteImpact,
          expanded: false,
        });
      });
    });

    setAlerts(list);
  }, [state]);

  return (
    <>
      <StyledButtonOpenDrawer onClick={() => setOpenDrawer(true)}>
        <Icon path={mdiMenuOpen} size={1.25} />
        <StyledAlertsNotification>
          <SeverityBadge severity={getPriorityAlert([{alerts}])} size={0.75} />
        </StyledAlertsNotification>
      </StyledButtonOpenDrawer>

      <StyledDrawer
        ModalProps={{
          container: document.getElementById('pos-wrapper'),
          style: {position: 'absolute'},
          disableEnforceFocus: true,
          BackdropProps: {
            invisible: true,
          },
        }}
        PaperProps={{
          style: {position: 'absolute'},
        }}
        anchor="right"
        transitionDuration={0}
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
      >
        <Grid>
          <StyledButtonCloseDrawer onClick={() => setOpenDrawer(false)}>
            <Icon path={mdiChevronRight} size={1.25} />
          </StyledButtonCloseDrawer>
        </Grid>

        <StyledGridTitle container>
          <SeverityBadge severity={getPriorityAlert([{alerts}])} />
          <span>Alerts</span>
          <StyledButtonCollapseAll onClick={handleToggleAllAlerts}>
            {allAlertsExpanded ? 'Collapse All' : 'Expand All'}
          </StyledButtonCollapseAll>
        </StyledGridTitle>
        <StyledAlertsContainer>
          {alerts.map(alert => (
            <StyledGridAlert key={`${alert.id}-${alert.expanded}`} container>
              <Accordion
                className="transparent"
                type={alert.alertType}
                severity={alert.siteImpact}
                header={
                  <AlertHeader
                    type={alert.alertType!}
                    title={alert.title}
                    subtitle={alert.shortDescription}
                    severity={alert.siteImpact}
                    deviceType={alert.deviceType}
                  />
                }
                expanded={alert.expanded}
                onExpandChange={(expanded: boolean) => handleExpandChange(expanded, alert.id)}
              >
                <DeviceDetails deviceType={alert.deviceType} scanResults={alert.scanResults} />
                <StyledAlertDetailsContainer>
                  {alert.description && (
                    <StyledAlertDetails>
                      <SGTitle className="t6">ISSUE</SGTitle>
                      <SGSubtitle className="s2">{alert.description}</SGSubtitle>
                    </StyledAlertDetails>
                  )}
                  {alert.effect && (
                    <StyledAlertDetails>
                      <SGTitle className="t6">EFFECT</SGTitle>
                      <SGSubtitle className="s2">{alert.effect}</SGSubtitle>
                    </StyledAlertDetails>
                  )}
                  {alert.solution && (
                    <StyledAlertDetails>
                      <SGTitle className="t6">SOLUTION</SGTitle>
                      <SGSubtitle className="s2">
                        {alert.solution} {alert.kb ?? ''}
                      </SGSubtitle>
                    </StyledAlertDetails>
                  )}
                </StyledAlertDetailsContainer>
              </Accordion>
            </StyledGridAlert>
          ))}
        </StyledAlertsContainer>
      </StyledDrawer>
    </>
  );
}
